import { useQuery, useMutation, useQueryClient } from 'react-query';
import { predictionManagerAxiosApi } from 'src/Config/axiosApi';

// ── Types — mirror the backend event_agent_runs doc + output contract ──

export interface ProgressEvent {
  ts: string;
  phase: string;
  message: string;
}

export interface AgentRunResult {
  status: 'completed' | 'partial' | 'failed';
  event?: {
    title?: string;
    kalshiEventTickers?: string[];
    polymarketEventSlug?: string;
    polymarketEventId?: string;
  };
  cachedMarketsUpserted?: { polymarket: number; kalshi: number };
  pairsCreated?: Array<{
    pairId?: string;
    name?: string;
    polymarketExternalId?: string;
    kalshiTicker?: string;
    marketType?: string;
    polarityInverted?: boolean;
    confidence?: number;
  }>;
  pairsSkipped?: Array<{
    polymarketExternalId?: string;
    kalshiTicker?: string;
    reason?: string;
  }>;
  unmatchedMarkets?: {
    polymarket?: Array<{
      externalId: string;
      question?: string;
      sportsMarketType?: string;
      reason: string;
    }>;
    kalshi?: Array<{ ticker: string; title?: string; reason: string }>;
  };
  gaps?: {
    aliases?: Array<Record<string, unknown>>;
    shapes?: Array<{ description?: string; affectedMarketCount?: number; prUrl?: string | null }>;
  };
  warnings?: string[];
}

/** Result of a fix-match run — the agent's verdict on one mis-matched pair. */
export interface FixMatchResult {
  status: 'completed' | 'failed';
  classification?: 'false_positive' | 'mis_lined' | 'not_a_bug';
  pairId?: string;
  polymarketExternalId?: string;
  kalshiTicker?: string;
  analysis?: string;
  action?: 'pr_opened' | 'no_change';
  prUrl?: string | null;
  correctedPair?: {
    polymarketExternalId?: string;
    kalshiTicker?: string;
    pairId?: string;
  } | null;
  warnings?: string[];
}

export interface EventAgentRun {
  _id: string;
  kind?: 'map-event' | 'fix-match';
  eventQuery: string;
  /** map-event only — optional direct event URLs the operator pasted in. */
  kalshiUrl?: string;
  polymarketUrl?: string;
  pairId?: string;
  reason?: string;
  fixMatchSnapshot?: {
    polymarketExternalId?: string;
    kalshiTicker?: string;
    pairName?: string;
  } | null;
  status: 'queued' | 'running' | 'completed' | 'failed';
  jobName?: string;
  progressLog?: ProgressEvent[];
  result: AgentRunResult | FixMatchResult | null;
  feedback?: Array<{ ts: string; missedPairs: unknown[]; status: string }>;
  error: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
}

interface RunsResponse {
  runs: EventAgentRun[];
  total: number;
}

// ── Hooks ──────────────────────────────────────────────────────────────

export interface StartRunInput {
  eventQuery: string;
  /** Optional direct event URLs to help the agent locate the event. */
  kalshiUrl?: string;
  polymarketUrl?: string;
}

/** Start a /map-event agent run. */
export function useStartEventAgentRun() {
  const qc = useQueryClient();
  return useMutation(
    async (input: StartRunInput) => {
      const { data } = await predictionManagerAxiosApi.post<EventAgentRun>(
        '/event-agent/runs',
        input,
      );
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('event-agent-runs');
      },
    },
  );
}

/**
 * Poll one run. Refetches every 1s while the run is queued/running, then
 * stops once it reaches a terminal state. The fast cadence is intentional:
 * the K8s dispatch is async on the backend and the agent streams progress
 * events while it works, so the operator wants live visibility.
 */
export function useEventAgentRun(id: string | null) {
  return useQuery(
    ['event-agent-run', id],
    async () => {
      const { data } = await predictionManagerAxiosApi.get<EventAgentRun>(
        `/event-agent/runs/${id}`,
      );
      return data;
    },
    {
      enabled: !!id,
      refetchInterval: (data) =>
        data && (data.status === 'queued' || data.status === 'running')
          ? 1_000
          : false,
    },
  );
}

/**
 * Start a fix-match run — review one mis-matched pair. Dispatched when the
 * operator unmatches a pair WITH a reason.
 */
export function useFixMatch() {
  const qc = useQueryClient();
  return useMutation(
    async ({ pairId, reason }: { pairId: string; reason: string }) => {
      const { data } = await predictionManagerAxiosApi.post<EventAgentRun>(
        '/event-agent/fix-match',
        { pairId, reason },
      );
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('event-agent-runs');
      },
    },
  );
}

/**
 * List recent runs (newest first). Auto-refreshes every 3s so a button that
 * surfaces in-flight runs (e.g. the Opportunities-page badge) keeps its
 * status counters live without the operator reopening the menu.
 */
export function useEventAgentRuns(limit = 20) {
  return useQuery(
    ['event-agent-runs', limit],
    async () => {
      const { data } = await predictionManagerAxiosApi.get<RunsResponse>(
        '/event-agent/runs',
        { params: { limit } },
      );
      return data;
    },
    { keepPreviousData: true, refetchInterval: 3_000 },
  );
}

export interface MissedPair {
  polymarketExternalId: string;
  kalshiTicker: string;
  polarityInverted?: boolean;
}

/** Submit operator corrections — pairs the agent missed. */
export function useSubmitEventAgentFeedback() {
  const qc = useQueryClient();
  return useMutation(
    async ({ runId, missedPairs }: { runId: string; missedPairs: MissedPair[] }) => {
      const { data } = await predictionManagerAxiosApi.post<EventAgentRun>(
        `/event-agent/runs/${runId}/feedback`,
        { missedPairs },
      );
      return data;
    },
    {
      onSuccess: (_data, vars) => {
        qc.invalidateQueries(['event-agent-run', vars.runId]);
      },
    },
  );
}
